import { UserProfile, useAuth } from '@shared/components/AuthProvider';
import { useEffect, useRef } from 'react';
import qs from '@shared/core/queryString';
import { useHistory } from '@react-router';
import { EventUserStatus } from '@graphql/generated';

/**
 * Mapping of scenario query param to redirect subpath
 */
const SCENARIO_MAPPING: Record<string, string> = {
  'save-the-date': '/edit/cards/wedding/save-the-date',
  invitation: '/edit/cards/wedding/invitation'
};

type EventMembership = UserProfile['eventMemberships'][number];

type ValidEventMembership = {
  id: string;
  event: NonNullable<EventMembership['event']>;
  status: EventUserStatus.admin | EventUserStatus.owner;
};

const isValidEventMembership = (eventMembership: EventMembership): eventMembership is ValidEventMembership => {
  const isOwnerOrAdmin = eventMembership.status === EventUserStatus.admin || eventMembership.status === EventUserStatus.owner;
  const hasEventWebsite = !!eventMembership.event?.website;
  return isOwnerOrAdmin && hasEventWebsite;
};

/**
 * When thre is a `scenario` query param in the URL and the user is logged in and has at least one event,
 * redirect the user to the appropriate scenario page.
 *
 * Notes:
 * - If the user has more than one event, redirect to the event picker page.
 * - If the user has only one event, redirect to the intended scenario page for that event.
 */
export const useAutoRedriectToScenario = () => {
  const { isCurrentUserSettled, isLoggedIn, currentUser } = useAuth();
  const scenarioRef = useRef(qs.getValueString('scenario'));
  const history = useHistory();
  const shouldStopConsideringRedirectRef = useRef(false);

  useEffect(() => {
    if (!isCurrentUserSettled || shouldStopConsideringRedirectRef.current) {
      return;
    }

    const eventsAsOwnerOrAdmin = currentUser.eventMemberships.filter(isValidEventMembership);
    const hasOneOrMoreEvents = eventsAsOwnerOrAdmin.length > 0;
    const redirectSubPath = scenarioRef.current && SCENARIO_MAPPING[scenarioRef.current];

    // If user is not logged in or has no events, do nothing -- they have to create an event.
    if (isLoggedIn && hasOneOrMoreEvents && redirectSubPath) {
      // - Only 1 event, take user to their target event
      // - Has more than 1 event, redirect to event picker
      const basePath = eventsAsOwnerOrAdmin.length === 1 ? `/${eventsAsOwnerOrAdmin[0].event.website}` : '/event';
      history.push(basePath + redirectSubPath + history.location.search);
    } else {
      // Prevent further auto redirects.
      shouldStopConsideringRedirectRef.current = true;
    }
  }, [isCurrentUserSettled, isLoggedIn, history, currentUser]);
};
